/**
 * Custom hook for fetching transactions
 * Extracted from Activity.tsx to improve code organization
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { auth } from "../../../firebase";
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  QuerySnapshot,
  Unsubscribe,
  FirestoreError,
  DocumentSnapshot,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { processTransactionSnapshot, TransactionWithDate } from "../../../utils/transactionProcessing";
import { setActivityTransactionsSeed } from "../../../utils/activityTransactionsSeed";
import { isDemoMode, getDemoWorkspaceId } from "../../../utils/demoMode";
import { isBypassAuthEnabled } from "../../../utils/bypassAuth";

export interface UseTransactionsResult {
  transactions: TransactionWithDate[];
  transactionsLoaded: boolean;
  hasMoreTransactions: boolean;
  lastTransactionDoc: DocumentSnapshot | null;
  allTransactionsRef: React.MutableRefObject<TransactionWithDate[]>;
  /** True when the first page is from cache (e.g. re-entering from Analytics); don't re-run history check before showing list */
  transactionsFromCache: boolean;
  /** Call after saving a transaction (e.g. edit + add fee) so the list refetches and shows the new/updated data */
  refreshTransactions: () => void;
}

export interface UseTransactionsParams {
  workspaceId: string;
  membershipEnsured: boolean;
}

// Cache for transactions per workspace - persists across component unmounts
const transactionsCache = new Map<string, {
  transactions: TransactionWithDate[];
  lastTransactionDoc: DocumentSnapshot | null;
  hasMoreTransactions: boolean;
  timestamp: number;
}>();

// Cache expiration time: 5 minutes
const CACHE_EXPIRY_MS = 5 * 60 * 1000;

// Initial page size for fast first load; "load more" fetches additional pages
export const TRANSACTIONS_PAGE_SIZE = 25;

/** Call after editing a transaction so Activity list refetches and shows the update when navigating back */
export function invalidateTransactionsCache(workspaceId: string): void {
  transactionsCache.delete(workspaceId);
}

/**
 * Hook for fetching transactions in real-time
 * Uses caching to avoid reloading data when navigating back to Activity
 */
export const useTransactions = ({
  workspaceId,
  membershipEnsured,
}: UseTransactionsParams): UseTransactionsResult => {
  // In demo mode, use demo workspace ID when prop is empty (e.g. initial render before layout sets it)
  const effectiveWorkspaceId = workspaceId || (isDemoMode() ? getDemoWorkspaceId() : "");
  // Check cache first
  const cachedData = transactionsCache.get(effectiveWorkspaceId);
  const hasValidCache = !!(cachedData && (Date.now() - cachedData.timestamp) < CACHE_EXPIRY_MS);
  
  const [transactions, setTransactions] = useState<TransactionWithDate[]>(
    hasValidCache && cachedData ? cachedData.transactions : []
  );
  const [transactionsLoaded, setTransactionsLoaded] = useState<boolean>(hasValidCache);
  const [hasMoreTransactions, setHasMoreTransactions] = useState<boolean>(
    hasValidCache && cachedData ? cachedData.hasMoreTransactions : true
  );
  const [lastTransactionDoc, setLastTransactionDoc] = useState<DocumentSnapshot | null>(
    hasValidCache && cachedData ? cachedData.lastTransactionDoc : null
  );
  const allTransactionsRef = useRef<TransactionWithDate[]>(
    hasValidCache && cachedData ? cachedData.transactions : []
  );
  const [proceedWithoutMembership, setProceedWithoutMembership] = useState<boolean>(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const transactionsFromCache = hasValidCache;
  const refreshTransactions = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    // Allow loading in demo mode or when authenticated
    if (!effectiveWorkspaceId || (!auth.currentUser && !isDemoMode() && !isBypassAuthEnabled())) {
      // Set all loaded flags to true to trigger loading state change
      setTransactionsLoaded(true);
      return;
    }

    // Start loading transactions immediately - don't wait for membership check
    // Permission errors are already handled gracefully in the error handler below
    // This ensures instant loading for better UX
    // Owners can access transactions even without member document (Firestore rules allow this)
    
    // If membership isn't ensured and we haven't decided to proceed, proceed immediately
    if (!membershipEnsured && !isDemoMode() && !proceedWithoutMembership) {
      setProceedWithoutMembership(true);
      // Continue to query setup below - don't return early
    }
    
    // Reset proceed flag when membership is ensured (cleanup)
    if (membershipEnsured && proceedWithoutMembership) {
      setProceedWithoutMembership(false);
    }
    
    // Only skip query if membership isn't ensured AND we haven't decided to proceed AND not in demo mode
    // This allows immediate loading for owners while still respecting membership check for other users
    if (!membershipEnsured && !proceedWithoutMembership && !isDemoMode()) {
      return;
    }

    // Query transactions from workspace subcollection - load first page only for fast first paint
    const transactionsRef = collection(db, "workspaces", effectiveWorkspaceId, "transactions");
    const q = query(
      transactionsRef,
      orderBy("date", "desc"),
      limit(TRANSACTIONS_PAGE_SIZE)
    );

    // Check cache again inside effect to ensure we have latest state
    const cachedData = transactionsCache.get(effectiveWorkspaceId);
    const hasValidCache = !!(cachedData && (Date.now() - cachedData.timestamp) < CACHE_EXPIRY_MS);

    // Only set loading if we don't have cached data
    if (!hasValidCache) {
      setTransactionsLoaded(false);
    }

    // Transaction processing functions moved to transactionProcessing.ts
    const handleSnapshot = (snapshot: QuerySnapshot): void => {
      const transactionsList = processTransactionSnapshot(snapshot);
      
      // Store last document for pagination
      const lastDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
      const hasMore = snapshot.docs.length === TRANSACTIONS_PAGE_SIZE; // If we got a full page, there might be more
      
      // Update cache
      transactionsCache.set(effectiveWorkspaceId, {
        transactions: transactionsList,
        lastTransactionDoc: lastDoc,
        hasMoreTransactions: hasMore,
        timestamp: Date.now(),
      });

      // Seed Analytics so opening Analytics from Activity shows data immediately
      setActivityTransactionsSeed(effectiveWorkspaceId, transactionsList);
      
      setLastTransactionDoc(lastDoc);
      setHasMoreTransactions(hasMore);
      
      // Store in ref for filtering
      allTransactionsRef.current = transactionsList;
      setTransactions(transactionsList);
      setTransactionsLoaded(true);
      // Don't set loading to false here - wait until ALL data (including members) is loaded
      // This prevents UI flicker when user names appear
      
      // Ensure transactions are set even if empty (so UI knows data has loaded)
      if (transactionsList.length === 0) {
        console.log("No transactions found for workspace:", effectiveWorkspaceId);
      }
    };

    let unsubscribe: Unsubscribe | undefined;

    try {
      unsubscribe = onSnapshot(
        q,
        handleSnapshot,
        (error: FirestoreError) => {
          console.error("Error fetching transactions:", error);
          // If error is about missing index, try simpler query without orderBy
          if (error.code === "failed-precondition" || error.message?.includes("index")) {
            const fallbackQ = query(transactionsRef);
            
            unsubscribe = onSnapshot(
              fallbackQ,
              handleSnapshot,
              (fallbackError: FirestoreError) => {
                console.error("Fallback query also failed:", fallbackError);
                setTransactions([]);
                setTransactionsLoaded(true);
                // Don't set loading to false here - wait until ALL data is loaded
              }
            );
          } else if (error.code === "permission-denied" || error.message?.includes("permissions")) {
            // Permission error - might be timing issue after accepting invitation or role issue
            console.warn("Permission error fetching transactions. This might be a timing issue after accepting an invitation or a role/permission issue. Retrying in 2 seconds...");
            // Ensure membership is set to true to allow retry
            setTimeout(() => {
              // Retry the query after a short delay
              const retryUnsubscribe = onSnapshot(
                q,
                handleSnapshot,
                (retryError: FirestoreError) => {
                  console.error("Retry query also failed:", retryError);
                  setTransactions([]);
                  setTransactionsLoaded(true);
                  // Don't set loading to false here - wait until ALL data is loaded
                  // If still failing, try without orderBy as fallback
                  if (retryError.code === "permission-denied" || retryError.message?.includes("permissions")) {
                    console.warn("Trying fallback query without orderBy...");
                    const fallbackQ = query(transactionsRef);
                    const fallbackUnsubscribe = onSnapshot(
                      fallbackQ,
                      handleSnapshot,
                      (fallbackError: FirestoreError) => {
                        console.error("Fallback query also failed:", fallbackError);
                        setTransactions([]);
                        setTransactionsLoaded(true);
                        // Don't set loading to false here - wait until ALL data is loaded
                      }
                    );
                    unsubscribe = fallbackUnsubscribe;
                  }
                }
              );
              unsubscribe = retryUnsubscribe;
            }, 2000);
          } else {
            console.error("Unknown error fetching transactions:", error);
            setTransactions([]);
            setTransactionsLoaded(true);
            // Don't set loading to false here - wait until ALL data is loaded
          }
        }
      );
    } catch (error) {
      console.error("Query setup error:", error);
      setTransactions([]);
      setTransactionsLoaded(true);
      // Don't set loading to false here - wait until ALL data is loaded
      return;
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [effectiveWorkspaceId, membershipEnsured, proceedWithoutMembership, refreshKey]);

  return {
    transactions,
    transactionsLoaded,
    hasMoreTransactions,
    lastTransactionDoc,
    allTransactionsRef,
    transactionsFromCache,
    refreshTransactions,
  };
};
